import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, AlertTriangle } from "lucide-react";

export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const navigate = useNavigate();
  const { } = useAuth();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-slate-900/90 border-slate-800 p-8 rounded-2xl" data-testid="reset-no-token">
          <div className="flex justify-center mb-6"><Logo size="lg" /></div>
          <div className="text-center">
            <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
            <h1 className="font-display text-xl font-bold mb-2">Link is missing</h1>
            <p className="text-sm text-slate-400 mb-5">This reset link is incomplete. Request a fresh one.</p>
            <Link to="/forgot-password" className="text-orange-400 hover:text-orange-300 font-medium">Get a new link</Link>
          </div>
        </Card>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) return toast.error("Password must be at least 8 characters");
    if (pwd !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, password: pwd });
      // Server returns an AuthResponse; apply it directly so the user lands logged in.
      localStorage.setItem("rooflead_token", data.token);
      toast.success("Password updated — you're signed in");
      // Hard reload so AuthContext picks the new token on mount
      window.location.replace("/app");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Could not reset password");
      if (err.response?.status === 400 && detail?.toLowerCase().includes("expired")) {
        setTimeout(() => navigate("/forgot-password"), 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
      <Card className="relative w-full max-w-md bg-slate-900/90 border-slate-800 p-8 rounded-2xl" data-testid="reset-password-card">
        <div className="mb-8 flex justify-center"><Logo size="lg" /></div>
        <h1 className="font-display text-2xl font-bold mb-2">Set a new password</h1>
        <p className="text-sm text-slate-400 mb-6">
          Pick something you'll remember. We'll sign you in automatically.
        </p>
        <form onSubmit={submit} className="space-y-4" data-testid="reset-password-form">
          <div>
            <Label htmlFor="pwd" className="text-slate-300">New password</Label>
            <Input id="pwd" data-testid="reset-password-input" type="password" value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              className="mt-1 bg-slate-800 border-slate-700 text-white"
              required minLength={8} autoFocus />
          </div>
          <div>
            <Label htmlFor="confirm" className="text-slate-300">Confirm password</Label>
            <Input id="confirm" data-testid="reset-password-confirm" type="password" value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 bg-slate-800 border-slate-700 text-white"
              required minLength={8} />
          </div>
          <Button type="submit" disabled={loading} data-testid="reset-submit-btn"
            className="w-full bg-orange-500 hover:bg-orange-600 text-slate-900 font-bold h-11">
            {loading ? "Updating…" : <>Reset password <ArrowRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </form>
        <p className="text-sm text-slate-400 mt-6 text-center">
          Back to <Link to="/login" className="text-orange-400 hover:text-orange-300 font-medium" data-testid="reset-goto-login">login</Link>
        </p>
      </Card>
    </div>
  );
}
